package com.schoolconnect.app.designsystem.list

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.composed
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.offset
import com.schoolconnect.app.util.BuildConfig
import com.schoolconnect.app.util.PlatformType

private const val SCROLLBAR_MIN_THUMB_DP = 28f
private const val SCROLLBAR_WIDTH_DP = 8f
private const val SCROLLBAR_END_PADDING_DP = 2f
private const val SCROLLBAR_TOUCH_WIDTH_DP = 18f
private const val SCROLLBAR_ALPHA = 0.72f

internal class AdminListVerticalScrollbarHost {
    var state by mutableStateOf<LazyListState?>(null)
}

internal val LocalAdminListVerticalScrollbarHost =
    compositionLocalOf<AdminListVerticalScrollbarHost?> { null }

internal fun isDesktopScrollablePlatform(): Boolean = when (BuildConfig.platform()) {
    PlatformType.DESKTOP,
    PlatformType.JS,
    PlatformType.WASM -> true
    else -> false
}

private data class LazyScrollbarMetrics(
    val topFraction: Float,
    val heightFraction: Float,
    val headerOffsetItems: Int,
    val totalScrollableItems: Int,
    val visibleScrollableItems: Int,
    val averageItemSizePx: Float,
)

private fun computeLazyScrollbarMetrics(state: LazyListState): LazyScrollbarMetrics? {
    val info = state.layoutInfo
    val total = info.totalItemsCount
    val visibleCount = info.visibleItemsInfo.size
    if (total <= 0 || visibleCount <= 0 || total <= visibleCount) return null

    val hasPinnedTopHeader = info.visibleItemsInfo.any { it.index == 0 } && info.visibleItemsInfo.any { it.index > 0 }
    val headerOffsetItems = if (hasPinnedTopHeader) 1 else 0

    val visibleScrollable = info.visibleItemsInfo.filter { it.index >= headerOffsetItems }
    val visibleScrollableCount = visibleScrollable.size.coerceAtLeast(1)
    val totalScrollable = (total - headerOffsetItems).coerceAtLeast(1)
    if (totalScrollable <= visibleScrollableCount) return null

    val maxFirst = (totalScrollable - visibleScrollableCount).coerceAtLeast(1)
    val minVisibleIndex = (visibleScrollable.minByOrNull { it.index }?.index ?: state.firstVisibleItemIndex)
        .minus(headerOffsetItems)
        .coerceAtLeast(0)
    val maxVisibleIndex = (visibleScrollable.maxByOrNull { it.index }?.index ?: state.firstVisibleItemIndex)
        .minus(headerOffsetItems)
        .coerceAtLeast(0)
    val firstIndexCandidate = (state.firstVisibleItemIndex - headerOffsetItems).coerceAtLeast(0)
    val tailBackComputed = (maxVisibleIndex - visibleScrollableCount + 1).coerceAtLeast(0)
    val anchorIndex = maxOf(firstIndexCandidate, minVisibleIndex, tailBackComputed).coerceAtMost(maxFirst)
    val top = (anchorIndex.toFloat() / maxFirst.toFloat()).coerceIn(0f, 1f)
    val height = (visibleScrollableCount.toFloat() / totalScrollable.toFloat()).coerceIn(0f, 1f)
    return LazyScrollbarMetrics(
        topFraction = top,
        heightFraction = height,
        headerOffsetItems = headerOffsetItems,
        totalScrollableItems = totalScrollable,
        visibleScrollableItems = visibleScrollableCount,
        averageItemSizePx = visibleScrollable.map { it.size }.average().toFloat().coerceAtLeast(1f),
    )
}

private fun computeThumbHeightPx(
    metrics: LazyScrollbarMetrics,
    viewportHeightPx: Float,
    minThumbPx: Float,
): Float = scrollbarThumbHeightPx(viewportHeightPx, metrics.heightFraction, minThumbPx)

internal fun scrollbarThumbHeightPx(viewportHeightPx: Float, heightFraction: Float, minThumbPx: Float): Float {
    val trackHeight = viewportHeightPx.coerceAtLeast(0f)
    return (trackHeight * heightFraction).coerceIn(minThumbPx.coerceIn(0f, trackHeight), trackHeight)
}

private fun computeThumbTopPx(
    metrics: LazyScrollbarMetrics,
    viewportHeightPx: Float,
    thumbHeightPx: Float,
): Float = ((viewportHeightPx - thumbHeightPx) * metrics.topFraction).coerceIn(0f, viewportHeightPx - thumbHeightPx)

/**
 * Handle wheel/trackpad scrolling on the table body before nested parents
 * (horizontal table scroll) or LazyColumn's own scrollable can also apply it.
 * Applying the delta once on the Initial pass avoids jumpy double-scroll.
 */
private fun Modifier.desktopWheelScroll(state: LazyListState): Modifier = pointerInput(state) {
    awaitPointerEventScope {
        while (true) {
            val event = awaitPointerEvent(PointerEventPass.Initial)
            if (event.type != PointerEventType.Scroll) continue

            val delta = event.changes.firstOrNull()?.scrollDelta?.y ?: 0f
            if (delta == 0f) continue

            val consumed = state.dispatchRawDelta(delta)
            if (consumed != 0f) {
                event.changes.forEach { it.consume() }
            }
        }
    }
}

@Composable
internal fun DesktopLazyScrollbarOverlay(
    state: LazyListState,
    endOffset: Dp = 0.dp,
    modifier: Modifier = Modifier,
) {
    val density = LocalDensity.current
    val minThumbPx = with(density) { SCROLLBAR_MIN_THUMB_DP.dp.toPx() }
    val barWidthPx = with(density) { SCROLLBAR_WIDTH_DP.dp.toPx() }
    val endPaddingPx = with(density) { SCROLLBAR_END_PADDING_DP.dp.toPx() }
    val metrics by remember(state) {
        derivedStateOf { computeLazyScrollbarMetrics(state) }
    }
    var dragGrabOffsetPx by remember { mutableStateOf(0f) }

    Box(
        modifier = modifier
            .fillMaxHeight()
            .width(SCROLLBAR_TOUCH_WIDTH_DP.dp)
            .offset(x = endOffset)
            .pointerInput(state) {
                detectDragGestures(
                    onDragStart = { offset ->
                        val currentMetrics = metrics ?: return@detectDragGestures
                        val trackHeight = size.height.toFloat()
                        if (trackHeight <= 0f) return@detectDragGestures
                        val thumbHeight = computeThumbHeightPx(currentMetrics, trackHeight, minThumbPx)
                        val thumbTop = computeThumbTopPx(currentMetrics, trackHeight, thumbHeight)
                        dragGrabOffsetPx = (offset.y - thumbTop).coerceIn(0f, thumbHeight)
                    },
                    onDrag = { change, dragAmount ->
                        change.consume()
                        val currentMetrics = metrics ?: return@detectDragGestures
                        val trackHeight = size.height.toFloat()
                        if (trackHeight <= 0f) return@detectDragGestures
                        val thumbHeight = computeThumbHeightPx(currentMetrics, trackHeight, minThumbPx)
                        if (thumbHeight <= 0f) return@detectDragGestures
                        val maxThumbTravel = (trackHeight - thumbHeight).coerceAtLeast(1f)
                        val maxScrollPx = (
                            (currentMetrics.totalScrollableItems - currentMetrics.visibleScrollableItems)
                                .coerceAtLeast(1) * currentMetrics.averageItemSizePx
                            )
                        // Apply drag deltas directly instead of starting a new
                        // scrollToItem coroutine for every pointer event.
                        state.dispatchRawDelta(
                            dragAmount.y * maxScrollPx / maxThumbTravel
                        )
                    },
                    onDragEnd = { dragGrabOffsetPx = 0f },
                    onDragCancel = { dragGrabOffsetPx = 0f },
                )
            }
            .drawWithContent {
                drawContent()
                val currentMetrics = metrics ?: return@drawWithContent
                val trackHeight = size.height
                if (trackHeight <= 0f) return@drawWithContent
                val thumbHeight = computeThumbHeightPx(currentMetrics, trackHeight, minThumbPx)
                val thumbTop = computeThumbTopPx(currentMetrics, trackHeight, thumbHeight)
                val thumbLeft = this.size.width - barWidthPx - endPaddingPx

                drawRoundRect(
                    color = Color(0xFF7A7A7A).copy(alpha = SCROLLBAR_ALPHA),
                    topLeft = Offset(thumbLeft, thumbTop),
                    size = Size(barWidthPx, thumbHeight),
                    cornerRadius = CornerRadius(barWidthPx / 2f, barWidthPx / 2f),
                )
            },
    )
}

@Composable
fun DesktopAwareLazyColumn(
    modifier: Modifier = Modifier,
    state: LazyListState = rememberLazyListState(),
    scrollbarEndOffset: Dp = 0.dp,
    contentPadding: PaddingValues = PaddingValues(0.dp),
    reverseLayout: Boolean = false,
    verticalArrangement: androidx.compose.foundation.layout.Arrangement.Vertical =
        if (!reverseLayout) androidx.compose.foundation.layout.Arrangement.Top else androidx.compose.foundation.layout.Arrangement.Bottom,
    horizontalAlignment: Alignment.Horizontal = Alignment.Start,
    userScrollEnabled: Boolean = true,
    content: LazyListScope.() -> Unit,
) {
    val scrollbarHost = LocalAdminListVerticalScrollbarHost.current
    Box(modifier = modifier) {
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .then(if (isDesktopScrollablePlatform()) Modifier.desktopWheelScroll(state) else Modifier),
            state = state,
            contentPadding = contentPadding,
            reverseLayout = reverseLayout,
            verticalArrangement = verticalArrangement,
            horizontalAlignment = horizontalAlignment,
            userScrollEnabled = userScrollEnabled,
            content = content,
        )
        if (isDesktopScrollablePlatform() && scrollbarHost == null) {
            Box(
                modifier = Modifier.fillMaxSize(),
            ) {
                DesktopLazyScrollbarOverlay(
                    state = state,
                    endOffset = scrollbarEndOffset,
                    modifier = Modifier.align(Alignment.CenterEnd),
                )
            }
        }
    }
}
